/**
 * Create Category Dialog Component
 * Form to create a new category
 */

import { useState } from 'react';
import { useCreateCategory } from '../../stores/categories';
import { useActiveUsersStore } from '../../stores/users';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface CreateCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateCategoryDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateCategoryDialogProps) {
  const [categoryName, setCategoryName] = useState('');
  const [defaultResponsiblePersonId, setDefaultResponsiblePersonId] = useState<number | null>(null);
  const [defaultDisposalAvenue, setDefaultDisposalAvenue] = useState('');
  const [error, setError] = useState('');

  const createMutation = useCreateCategory();
  useActiveUsersStore.live();
  const users = useActiveUsersStore(s => s.activeUsers);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      await createMutation.mutate({
        category_name: categoryName.trim(),
        default_responsible_person_id: defaultResponsiblePersonId,
        default_disposal_avenue: defaultDisposalAvenue || null,
      });
      handleClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    }
  }

  function handleClose() {
    setCategoryName('');
    setDefaultResponsiblePersonId(null);
    setDefaultDisposalAvenue('');
    setError('');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Category</DialogTitle>
          <DialogDescription>
            Add a new asset category to organize your inventory.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category-name">Category Name *</Label>
              <Input
                id="category-name"
                type="text"
                placeholder="e.g., Electronics, Furniture, Artwork"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                required
                disabled={createMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsible-person">Default Responsible Person</Label>
              <Select
                value={defaultResponsiblePersonId?.toString() || 'none'}
                onValueChange={(value) =>
                  setDefaultResponsiblePersonId(value === 'none' ? null : parseInt(value, 10))
                }
              >
                <SelectTrigger disabled={createMutation.isPending}>
                  <SelectValue placeholder="Select a person (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users?.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id.toString()}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="disposal-avenue">Default Disposal Avenue</Label>
              <Input
                id="disposal-avenue"
                type="text"
                placeholder="e.g., Auction, Online Marketplace"
                value={defaultDisposalAvenue}
                onChange={(e) => setDefaultDisposalAvenue(e.target.value)}
                disabled={createMutation.isPending}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
